import React, { useEffect, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect
} from 'react-router-dom';
import { SpinLoading } from 'antd-mobile';
import { useSnapshot } from 'valtio';
import AuthenticatedLayout from './layouts/AuthenticatedLayout.tsx';
import { appStateProxy } from '@/hooks/useAppState.ts';
import { initializeAppState } from '@/services/appInitializer.ts';
import { useViewportHeight } from '@/hooks/useViewportHeight.ts';
import ErrorBoundary from '@components/ErrorBoundary.tsx';

// Phase 1: Lazy load role components and routes for optimal bundle splitting
// Superadmins don't download seller code, sellers don't download superadmin code
const Login = React.lazy(() => import('../pages/Login/Login.tsx'));
const Register = React.lazy(() => import('../pages/Register/Register.tsx'));
const Seller = React.lazy(() => import('../pages/roles/seller/Seller.tsx'));
const SuperAdmin = React.lazy(() => import('../pages/roles/superadmin/SuperAdmin.tsx'));
const ProductionWorker = React.lazy(() => import('../pages/roles/production_worker/ProductionWorker.tsx'));
// const InvoiceStylePreview = React.lazy(() => import('@/pages/Orders/Invoice/InvoiceStylePreview.tsx'));

const AppRouter: React.FC = () => {
  const appStateSnap = useSnapshot(appStateProxy);
  const [initError, setInitError] = React.useState(false);
  useViewportHeight();

  useEffect(() => {
    // Initialize app state on mount with timeout fallback
    const timeoutId = setTimeout(() => {
      if (appStateSnap.isInitializing) {
        console.error('AppRouter: Initialization timeout after 10 seconds');
        setInitError(true);
      }
    }, 10000); // 10 second timeout
    
    initializeAppState()
      .then((success) => {
        clearTimeout(timeoutId);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        console.error('AppRouter: Initialization failed', error);
        setInitError(true);
      });
    
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show error if initialization times out or fails
  if (initError) {
    console.error('AppRouter: Initialization error, forcing render');
    // Force render routes even if initialization failed
    // AuthenticatedLayout will handle redirects
  }
  // Show loading spinner while initializing (but not forever)
  else if (appStateSnap.isInitializing) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        width: '100vw'
      }}>
        <SpinLoading style={{ '--size': '32px' }} color="var(--spona-brand-primary)" />
      </div>
    );
  }

  const userRole = appStateSnap.user?.role;

  return (
    <Router>
      <Switch>
        {/* Public routes */}
        {/* <Route path="/dev/invoice-preview">
          <Suspense fallback={null}>
            <InvoiceStylePreview />
          </Suspense>
        </Route> */}
        
        {/* Login route - redirect to home if already authenticated */}
        <Route path='/login'>
          <Suspense fallback={null}>
            {appStateSnap.user ? <Redirect to="/" /> : <Login />}
          </Suspense>
        </Route>

        {/* Register route - redirect to home if already authenticated */}
        <Route path='/register'>
          <Suspense fallback={null}>
            {appStateSnap.user ? <Redirect to="/" /> : <Register />}
          </Suspense>
        </Route>
        
        {/* Protected routes - require authentication */}
        <Route path="/">
          <AuthenticatedLayout>
            <ErrorBoundary sectionName="Application">
              <Suspense fallback={null}>
                {userRole === 'superadmin' ? (
                  <SuperAdmin />
                ) : userRole === 'production_worker' ? (
                  <ProductionWorker />
                ) : (
                  <Seller />
                )}
              </Suspense>
            </ErrorBoundary>
          </AuthenticatedLayout>
        </Route>
      </Switch>
    </Router>
  );
};

export default AppRouter;